import * as React from 'react'
import {Row, Tabs, notification} from 'antd';
import {GlossaryTabContent} from "./GlossaryTabContent";
import {useState, useEffect, useMemo} from "react";
import styles from './Glossary.module.scss'
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    clearGlossary,
    fetchDatasets,
    fetchGlossary,
    GlossaryData,
    groupGlossary,
    selectGlossary
} from "./glossarySlice";
import {GlossaryTerm} from "../../actions/api";
import { GlossarySearch } from './GlossarySearch';

export type FocusedTerm = {
    title: string,
    searchTitle: string
}

export type SetFocusedTerm = (term: FocusedTerm) => void

const filterGlossaryByDataset = (
    glossary: GlossaryTerm[] | undefined,
    datasetId: number | undefined
): GlossaryTerm[] | undefined => {
    if (glossary === undefined) {
        return undefined
    }

    if (datasetId === undefined) {
        return glossary
    }

    return glossary.filter(term => term.datasets.findIndex(dataset => dataset.id === datasetId) >  -1)
}

const renderTabBar = (props: any, DefaultTabBar: any) => <DefaultTabBar {...props} className={styles['glossary-tab-bar']}/>


const GlossaryTabs = ({focusedTerm, setFocusedTerm, glossary, selectedDataset, setSelectedDataset}: {
    focusedTerm: FocusedTerm,
    setFocusedTerm: SetFocusedTerm
    glossary: GlossaryData | undefined
    selectedDataset: number | undefined
    setSelectedDataset: (datasetId: number | undefined) => void
}) => {
    const [activeKey, setActiveKey] = useState("0")

    return (<>
        <Tabs
            renderTabBar={renderTabBar}
            onChange={(tabIndex) => {
                if (glossary === undefined) {
                    return
                }
                setActiveKey(tabIndex)
                setFocusedTerm({
                    title: glossary[parseInt(tabIndex)].terms[0].title,
                    searchTitle: focusedTerm.searchTitle
                })
            }}
            defaultActiveKey="0"
            activeKey={activeKey}
            items={
                (glossary ?? []).map((group, index) => {
                    return {
                        key: `${index}`,
                        label: group.key,
                        className: styles.glossaryTabPane
                    }
                })
            }
        >
        </Tabs>
        <GlossaryTabContent
            glossary={glossary}
            focusedTerm={focusedTerm}
            setFocusedTerm={setFocusedTerm}
            setActiveKey={setActiveKey}
            selectedDataset={selectedDataset}
            setSelectedDataset={setSelectedDataset}
        />
    </>)
}

const Glossary = () => {
    const dispatch = useAppDispatch()
    const [focusedTerm, setFocusedTerm] = useState<FocusedTerm>({title: '', searchTitle: ''})
    const [selectedDataset, setSelectedDataset] = useState<number>()
    const glossary = useAppSelector(selectGlossary)
    const filteredGlossary = useMemo(() => filterGlossaryByDataset(glossary, selectedDataset), [glossary, selectedDataset])
    const glossaryGroups = useMemo(() => groupGlossary(filteredGlossary), [filteredGlossary])

    useEffect(() => {
        Promise.all([
            dispatch(fetchGlossary()).unwrap(),
            dispatch(fetchDatasets()).unwrap()
        ]).catch((e) => {
            console.error(e)
            notification.error({
                message: "Error loading glossary",
                description: "An error occurred while loading the glossary, please try again later."
            })
        })
        return () => {
            dispatch(clearGlossary)
        }
    }, [dispatch])

    return (
        <div className={styles.glossaryContent}>
            <Row className={styles['search-panel']} justify="center">
                <GlossarySearch
                    glossary={glossary ?? []}
                    setFocusedTerm={setFocusedTerm}
                    selectedDataset={selectedDataset}
                    setSelectedDataset={setSelectedDataset}
                />
            </Row>
            <GlossaryTabs
                focusedTerm={focusedTerm}
                setFocusedTerm={setFocusedTerm}
                glossary={glossaryGroups}
                selectedDataset={selectedDataset}
                setSelectedDataset={setSelectedDataset}
            />
        </div>
    )
}

export default Glossary